import React, { useCallback, useEffect, useState } from 'react';

import { Button as AntdButton, Modal as AntdModal } from 'antd';

import {
  CreateUserRequestDtoProfileEnum,
  CreateUserResultDto,
  UserDto,
  UserDtoProfileEnum,
} from 'lib_api/lib/api/gen';

import { Form } from 'components/WrappedComponents';
import { useApi } from 'hooks/ApiStoreContext';
import { useUserStore } from 'hooks/UserStoreContext';
import { CreationCompteFormValues } from 'types/Compte/CreationCompte';

import { WaitingReferentielLoadingField } from '../../WrappedComponents/Form/FormFields/Select/Referentiels/WaitingReferentielLoadingField';
import { getIdCorrelationEntiteUser } from '../common';

import CheckboxActif from './Fields/CheckboxActif';
import CheckboxGestionnaireLocal from './Fields/CheckboxGestionnaireLocal';
import InputEmail from './Fields/InputEmail';
import InputNom from './Fields/InputNom';
import InputPrenom from './Fields/InputPrenom';
import SelectProfile from './Fields/SelectProfile';
import SelectUniteRattachement from './Fields/SelectUniteRattachement';
import {
  generateCreateUserRequestDtoFromFormData,
  isCheckboxGestionnaireLocalEnabled,
  useSubmitCreateUser,
  useValidateCreateUser,
} from './utils';

interface ModalCreationProps {
  close: () => void;
  callAfterSubmit: (
    result: CreateUserResultDto,
    email: string | undefined,
  ) => void;
  visible: boolean;
}

const getDefaultData = (user: UserDto): CreationCompteFormValues => {
  const userProfile: CreateUserRequestDtoProfileEnum | undefined =
    user.profile === UserDtoProfileEnum.ADMIN_FONCTIONNEL
      ? undefined
      : (user.profile as unknown as CreateUserRequestDtoProfileEnum);

  return {
    actif: true,
    nom: undefined,
    prenom: undefined,
    email: undefined,
    profile: userProfile,
    uniteRattachement: getIdCorrelationEntiteUser(user),
    gestionnaireLocal: undefined,
  };
};

const ModalCreation = ({
  close,
  callAfterSubmit,
  visible,
}: ModalCreationProps): React.ReactElement => {
  const user = useUserStore();
  const defaultData = getDefaultData(user);
  const [data, setData] = useState(defaultData);
  const [
    checkboxGestionnaireLocalEnabled,
    setCheckboxGestionnaireLocalEnabled,
  ] = useState<boolean>(isCheckboxGestionnaireLocalEnabled(data));
  const refController = useApi().ReferentielControllerApi;

  const [submitForm, thenSubmmit, catchSubmit] = useSubmitCreateUser(result => {
    close();
    callAfterSubmit(result, data.email);
  });

  const memoSubmit = useCallback(async () => {
    return submitForm(data);
  }, [data, submitForm]);

  const [validateAndSubmit] = useValidateCreateUser(
    memoSubmit,
    thenSubmmit,
    catchSubmit,
  );

  const disabled = user.profile !== UserDtoProfileEnum.ADMIN_FONCTIONNEL;

  const updateFormValues = useCallback(
    (newValues: CreationCompteFormValues) => {
      // Clear entite rattachement if the type of the profile changes
      if (data.profile !== newValues.profile) {
        newValues.uniteRattachement = undefined;
      }
      const gestionnaireLocalEnabled =
        isCheckboxGestionnaireLocalEnabled(newValues);
      setCheckboxGestionnaireLocalEnabled(gestionnaireLocalEnabled);
      if (!gestionnaireLocalEnabled) {
        newValues.gestionnaireLocal = undefined;
      }
      setData(newValues);
    },
    [data],
  );

  const onCancel = () => {
    close();
    setData(defaultData);
  };

  const [PMtoSearch, setPMtoSearch] = useState<undefined | string>(undefined);
  const [searchEntiteLoaded, setSearchEntiteLoaded] = useState(false);

  useEffect(() => {
    if (
      data.profile === CreateUserRequestDtoProfileEnum.FORCE_DE_L_ORDRE &&
      data.uniteRattachement !== undefined
    ) {
      refController
        .getValidUniteFOByIdCorrelationUsingGET(data.uniteRattachement)
        .then(fetchedReferentiel => {
          setPMtoSearch(fetchedReferentiel.abreviation);
          setSearchEntiteLoaded(true);
        })
        .catch(() => {
          return;
        });
    } else {
      setSearchEntiteLoaded(true);
    }
  }, [data.profile, data.uniteRattachement, refController]);

  return (
    <AntdModal
      open={visible}
      onCancel={onCancel}
      footer={null}
      destroyOnClose={true}
      width={680}
      maskClosable={false}
      closable={true}
      title={<div className={'centered'}>Créer un nouveau compte</div>}
    >
      <Form
        id="addUserForm"
        initialValue={data}
        onChange={updateFormValues}
        onSubmit={async () => {
          await validateAndSubmit(cuController =>
            cuController.validateCreateUsingPOST(
              generateCreateUserRequestDtoFromFormData(data),
            ),
          );
          setData(defaultData);
        }}
        validateButtonTitle={'Ajouter'}
        validateButtonClassName={'ValidateFormButton'}
      >
        {(values, SubmitButton): React.ReactElement => {
          return (
            <>
              <InputNom values={values} />
              <InputPrenom values={values} />
              <InputEmail values={values} />
              <CheckboxActif values={values} />
              <SelectProfile values={values} disabled={disabled} />
              {searchEntiteLoaded ? (
                <SelectUniteRattachement
                  values={values}
                  selectedProfile={values.profile}
                  disabled={disabled}
                  PMtoSearch={PMtoSearch}
                />
              ) : (
                <WaitingReferentielLoadingField
                  title={'Entité de rattachement'}
                />
              )}
              {checkboxGestionnaireLocalEnabled && (
                <CheckboxGestionnaireLocal values={values} />
              )}
              <div className="ModalFooter">
                <AntdButton onClick={onCancel}>Annuler</AntdButton>
                {SubmitButton}
              </div>
            </>
          );
        }}
      </Form>
    </AntdModal>
  );
};

export default ModalCreation;
